import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import {
  centsToInput,
  eurosToCents,
  formatCents,
  useAddAdminBillingPayment,
  useDeleteAdminBillingPayment,
  type AdminBillingInvoiceDetail,
} from '../../hooks/use-admin-billing'
import { apiErrorMessage } from './billing-errors'

const METHODS = ['transfer', 'direct_debit', 'card', 'cheque', 'other'] as const

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** BillingInvoicePayments lists the payments and records a new one on an issued invoice. */
export function BillingInvoicePayments({ invoice }: { invoice: AdminBillingInvoiceDetail }) {
  const { t, i18n } = useTranslation('admin')
  const add = useAddAdminBillingPayment()
  const remove = useDeleteAdminBillingPayment()
  const [amount, setAmount] = useState<string | null>(null)
  const [paidOn, setPaidOn] = useState(today())
  const [method, setMethod] = useState<(typeof METHODS)[number]>('transfer')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [more, setMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const issued = invoice.status === 'issued'
  const shown = amount ?? centsToInput(invoice.remaining_cents)

  const record = (cents: number, on: string) => {
    setError(null)
    add.mutate(
      {
        params: { path: { uid: invoice.uid } },
        body: { amount_cents: cents, paid_on: on, method, reference: reference || null, note: note || null },
      },
      {
        onSuccess: () => {
          setAmount(null)
          setReference('')
          setNote('')
        },
        onError: (e) => setError(apiErrorMessage(e, t('billing.invoices.payment.error'))),
      },
    )
  }
  const submit = () => {
    const cents = eurosToCents(shown)
    if (!cents) {
      setError(t('billing.invoices.payment.invalid'))
      return
    }
    record(cents, paidOn)
  }

  if (!issued && invoice.payments.length === 0) return null

  return (
    <section className="space-y-4" data-testid="billing-invoice-payments">
      <h2 className="text-lg font-semibold">{t('billing.invoices.payments_title')}</h2>
      {invoice.payments.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.invoices.payment.date')}</TableHead>
                <TableHead>{t('billing.invoices.payment.method')}</TableHead>
                <TableHead>{t('billing.invoices.payment.reference')}</TableHead>
                <TableHead className="text-right">{t('billing.consumption.column.amount')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.payments.map((p) => (
                <TableRow key={p.uid} data-testid={`billing-payment-row-${p.uid}`}>
                  <TableCell>{p.paid_on}</TableCell>
                  <TableCell>{t(`billing.invoices.method.${p.method}`)}</TableCell>
                  <TableCell>{[p.reference, p.note].filter(Boolean).join(' · ')}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCents(p.amount_cents, i18n.language)}</TableCell>
                  <TableCell className="text-right">
                    <DeleteConfirmDialog
                      title={t('billing.invoices.payment.delete_title')}
                      description={t('billing.invoices.payment.delete_description')}
                      isDeleting={remove.isPending}
                      onConfirm={() => remove.mutate({ params: { path: { uid: p.uid } } })}
                      trigger={
                        <Button variant="ghost" size="sm" data-testid={`billing-payment-delete-${p.uid}`}>
                          {t('billing.prices.delete')}
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {issued && invoice.remaining_cents > 0 && (
        <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="billing-payment-form">
          <div className="space-y-1">
            <Label>{t('billing.invoices.payment.amount')}</Label>
            <Input value={shown} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="w-36" data-testid="billing-payment-amount" />
            <div className="text-xs text-muted-foreground" data-testid="billing-payment-remaining">
              {t('billing.invoices.payment.remaining', { amount: formatCents(invoice.remaining_cents, i18n.language) })}
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('billing.invoices.payment.date')}</Label>
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="w-44" data-testid="billing-payment-date" />
          </div>
          <div className="space-y-1">
            <Label>{t('billing.invoices.payment.method')}</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as (typeof METHODS)[number])}>
              <SelectTrigger className="w-44" data-testid="billing-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`billing.invoices.method.${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setMore((m) => !m)} data-testid="billing-payment-more-toggle">
            {t(more ? 'billing.invoices.payment.less' : 'billing.invoices.payment.more')}
          </Button>
          {more && (
            <div className="w-full flex flex-wrap items-end gap-3" data-testid="billing-payment-more">
              <div className="space-y-1">
                <Label>{t('billing.invoices.payment.reference')}</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} className="w-44" data-testid="billing-payment-reference" />
              </div>
              <div className="space-y-1 grow">
                <Label>{t('billing.invoices.payment.note')}</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="billing-payment-note" />
              </div>
            </div>
          )}
          <Button onClick={submit} disabled={add.isPending} data-testid="billing-payment-add">
            {t('billing.invoices.payment.add')}
          </Button>
          <Button
            variant="outline"
            onClick={() => record(invoice.remaining_cents, today())}
            disabled={add.isPending}
            data-testid="billing-payment-mark-paid"
          >
            {t('billing.invoices.payment.mark_paid')}
          </Button>
          {error && (
            <div className="w-full text-sm text-destructive" data-testid="billing-payment-error">
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
